<template lang="pug">
.thinking(:class="{ 'is-open': open, 'is-live': streaming }")
	button.thinking-toggle(
		type="button"
		:aria-expanded="open ? 'true' : 'false'"
		@click="open = !open"
	)
		span.thinking-label(:class="{ 'is-shiny': streaming }") {{ label }}
		v-icon.thinking-caret(:class="{ 'is-open': open }") mdi-chevron-right
	.thinking-body.markdown(v-if="open && text" v-html="html")
</template>
<script>
import { extractThinkingTitle } from '../../utils/thinkingTitle';

/**
 * Collapsible thinking block with Cursor-style shiny text, then a duration label.
 */
export default {
	name: 'AiChatThinking',
	props: {
		text: { type: String, default: '' },
		html: { type: String, default: '' },
		streaming: { type: Boolean, default: false },
		durationSec: { type: Number, default: null }
	},
	/**
	 * Expand state, last streamed title, and thinking timer.
	 * @returns {{ open: boolean, heldTitle: string, startedAt: number|null, elapsedSec: number|null }}
	 */
	data() {
		return {
			open: false,
			heldTitle: '',
			startedAt: null,
			elapsedSec: null
		};
	},
	computed: {
		/**
		 * Complete title token from the thinking body, if one has arrived.
		 * @returns {string}
		 */
		extracted() {
			return extractThinkingTitle(this.text, this.streaming) || '';
		},
		/**
		 * Seconds to show after thinking ends (parent stamp, else local timer).
		 * @returns {number|null}
		 */
		doneSec() {
			if (this.durationSec != null) return this.durationSec;
			return this.elapsedSec;
		},
		/**
		 * Live title while streaming, or "Thought for x seconds" when done.
		 * @returns {string}
		 */
		label() {
			if (this.streaming) return this.extracted || this.heldTitle || 'Thinking';
			if (this.doneSec == null) return 'Thought for a few seconds';
			if (this.doneSec === 1) return 'Thought for 1 second';
			return 'Thought for ' + this.doneSec + ' seconds';
		}
	},
	watch: {
		/**
		 * Remember the last complete title so the header does not flicker.
		 * @param {string} val
		 */
		extracted(val) {
			if (val) this.heldTitle = val;
		},
		streaming: {
			immediate: true,
			/**
			 * Start or stop the thinking timer when the stream state changes.
			 * @param {boolean} isStreaming
			 */
			handler(isStreaming) {
				this.syncDuration(isStreaming);
			}
		}
	},
	methods: {
		/**
		 * Record thinking start time, then store elapsed seconds when thinking ends.
		 * @param {boolean} isStreaming
		 */
		syncDuration(isStreaming) {
			if (isStreaming) {
				if (this.startedAt == null) this.startedAt = Date.now();
				return;
			}
			if (this.startedAt == null) return;
			this.elapsedSec = Math.max(1, Math.ceil((Date.now() - this.startedAt) / 1000));
			this.startedAt = null;
		}
	}
};
</script>
